import { useEffect, useMemo, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import ModelService from 'app/admin/model/service/ModelService';
import nBossConnection from 'modules/base/service/nBossConnection';
import { showMessage } from 'app/store/fuse/messageSlice';
import { Button } from '@material-ui/core';
import { FaFile, FaFileUpload, FaFileDownload, FaTrash } from 'react-icons/fa';

function EntriesListComponent(props) {
    const [list, setList] = useState(null);

    useEffect(() => {
        getList();
    }, []);

    const getList = () => {
        ModelService.listEntries(props.id).then(response => setList(response)).catch(err => console.log(err));
    };

    const groupedList = useMemo(() => {
        if (!list || !Array.isArray(list)) return [];

        const map = {};

        for (let i = 0; i < list.length; i += 1) {
            const item = list[i];
            const versionKey = String(item.version);

            if (!map[versionKey]) {
                map[versionKey] = {
                    version: item.version,
                    model_id: item.model_id,
                    items: []
                };
            }

            map[versionKey].items.push(item);
        }

        return Object.values(map).sort((a, b) => Number(b.version) - Number(a.version));
    }, [list]);

    return (
        <div className='w-full h-full flex flex-col gap-16'>

            <Uploader id={props.id} method={props.method} types={props.types} onSuccess={() => getList()} onFail={() => getList()} />

            {(groupedList && groupedList.length > 0) && <div className='flex flex-col gap-16'>
                {groupedList.map((item) => <EntryVersionItem key={item.version} data={item} parentId={props.id} types={props.types} onDeleteSuccess={() => getList()} />)}
            </div>}

        </div>
    );
}

function Uploader(props) {
    const dispatch = useDispatch();

    const [fileFieldList, setFileFieldList] = useState(props.types || []);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadingPercentage, setUploadingPercentage] = useState(0);
    const [rid, setRid] = useState(Math.random().toString(36));

    useEffect(() => {
        setFileFieldList((props.types || []).map(item => ({ ...item, file: null })));
    }, [props.types, rid]);

    const addEntry = () => {
        const formData = new FormData();
        formData.append('id', props.id);

        for (let i = 0; i < fileFieldList.length; i += 1)
            formData.append(fileFieldList[i].fieldName, fileFieldList[i].file, fileFieldList[i].file.name);

        setIsUploading(true);

        nBossConnection.connection.post(props.method, formData, {
            onUploadProgress: progressEvent => {
                if (!progressEvent.total) return;
                setUploadingPercentage((progressEvent.loaded / progressEvent.total) * 100);
            }
        }).then(() => {
            setIsUploading(false);
            setUploadingPercentage(0);
            setRid(Math.random().toString(36));
            dispatch(showMessage({ message: 'Entry successfully added' }));
            props.onSuccess();
        }).catch(e => {
            console.log(e);
            setIsUploading(false);
            setUploadingPercentage(0);
            dispatch(showMessage({ message: 'Upload failed, please check the data!' }));
            props.onFail();
        });
    };

    const handleFileChange = (file, fieldName) => {
        if (!fileFieldList) return;

        const _fileFieldList = [...fileFieldList];

        for (let i = 0; i < _fileFieldList.length; i += 1)
            if (_fileFieldList[i].fieldName === fieldName)
                _fileFieldList[i] = { ..._fileFieldList[i], file };

        setFileFieldList(_fileFieldList);
    };

    const isValid = () => {
        for (let i = 0; i < fileFieldList.length; i += 1)
            if (!fileFieldList[i].file)
                return false;
        return true;
    };

    return (
        <div className='w-full flex flex-col gap-16'>

            <div className='w-full flex flex-col gap-12'>
                {fileFieldList.map((item) => <UploadField key={item.id || item.fieldName} rid={rid} data={item} onFileChange={(file) => handleFileChange(file, item.fieldName)} />)}
            </div>

            <div className='w-full flex items-center gap-12'>
                <Button className='w-full' color='primary' onClick={() => { addEntry(); }} variant='contained' disabled={!isValid() || isUploading}>
                    {isUploading ? `Uploading... ${Math.round(uploadingPercentage)}%` : 'Add Entry'}
                </Button>
            </div>

        </div>
    );
}

function UploadField(props) {
    const [selectedFile, setSelectedFile] = useState(null);

    useEffect(() => {
        setSelectedFile(null);
    }, [props.rid]);

    useEffect(() => {
        props.onFileChange(selectedFile);
    }, [selectedFile]);

    const handleFileChange = (e) => {
        if (e.target.files) setSelectedFile(e.target.files[0]);
    };

    const formatFileSize = (bytes, si = false, dp = 1) => {
        const thresh = si ? 1000 : 1024;
        if (Math.abs(bytes) < thresh) { return bytes + ' B'; }
        const units = si ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
        let u = -1;
        const r = 10 ** dp;
        do { bytes /= thresh; ++u; } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);
        return bytes.toFixed(dp) + ' ' + units[u];
    };

    return (
        <div className='flex-1 relative flex items-center gap-10 px-16 py-8 shadow rounded-lg overflow-hidden transition-colors bg-white hover:bg-gray-50'>

            <FaFileUpload className='text-lg opacity-20' />

            <input key={props.rid} type='file' onChange={handleFileChange} className='absolute inset-0 w-full h-full cursor-pointer z-10' style={{ opacity: 0.0001 }} accept={props.data.accept} />

            {!selectedFile && <div className='font-medium'>Select {props.data.name || 'a'} file...&nbsp;&nbsp;{props.data.accept ? <span className='font-normal opacity-50'>Accepted file types: {props.data.accept.replaceAll(',', ', ')}</span> : ''}</div>}

            {selectedFile && <div className='w-full flex overflow-hidden'>
                <div className='font-semibold overflow-ellipsis'>Selected&nbsp;file:&nbsp;</div>
                <div className='font-normal overflow-ellipsis overflow-hidden whitespace-nowrap'>{selectedFile.name}</div>
                <div className='font-normal opacity-50 whitespace-nowrap'>&nbsp;({formatFileSize(selectedFile.size)})</div>
            </div>}

        </div>
    );
}

function EntryVersionItem(props) {
    const dispatch = useDispatch();
    const [isDeleting, setIsDeleting] = useState(false);

    const normalizeTypeKey = (value) => {
        const normalized = String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const withoutFileSuffix = normalized.endsWith('file') ? normalized.slice(0, -4) : normalized;
        const aliases = {
            worksheet: 'spreadsheet',
            xls: 'spreadsheet',
            xlsx: 'spreadsheet'
        };

        return aliases[withoutFileSuffix] || withoutFileSuffix;
    };

    const getUrl = (type) => {
        return window.location.protocol + '//' + window.location.host + '/mol-ocu-portal/methods/modelControl/getEntryData?modelId=' + props.parentId + '&version=' + props.data.version + '&type=' + type;
    };

    const getTypeItemKeys = (typeItem) => {
        return [
            typeItem?.type,
            typeItem?.entryType,
            typeItem?.fieldName,
            typeItem?.name
        ].map(normalizeTypeKey).filter(Boolean);
    };

    const getItemByTypeItem = (typeItem) => {
        const keys = getTypeItemKeys(typeItem);

        return (props.data.items || []).find(item => keys.includes(normalizeTypeKey(item.type))) || null;
    };

    const displayTypes = useMemo(() => {
        const baseTypes = props.types || [];
        const result = [...baseTypes];

        (props.data.items || []).forEach((entry) => {
            const isAlreadyDisplayed = baseTypes.some(typeItem => getItemByTypeItem(typeItem)?.id === entry.id);

            if (!isAlreadyDisplayed) {
                result.push({
                    name: entry.type,
                    fieldName: entry.type,
                    type: entry.type
                });
            }
        });

        return result;
    }, [props.types, props.data.items]);

    const handleDelete = () => {
        if (isDeleting) return;

        setIsDeleting(true);

        ModelService.deleteEntriesByModelIdAndEntryVersion(props.parentId, props.data.version).then(() => {
            setIsDeleting(false);
            dispatch(showMessage({ message: 'Entry version successfully deleted' }));
            props.onDeleteSuccess();
        }).catch((err) => {
            console.log(err);
            setIsDeleting(false);
            dispatch(showMessage({ message: 'Delete failed' }));
        });
    };

    return (
        <div className='w-full flex rounded-lg border overflow-hidden bg-white'>

            {/* <div className='flex items-center justify-center py-12 px-20 border-r'>
                <FaFile className='text-lg opacity-20' />
            </div> */}

            <div className='flex-1 p-12 flex flex-col gap-8'>

                <div className='text-sm font-semibold text-gray-700'>Version {props.data.version}</div>

                <div className='w-full grid gap-8' style={{ gridTemplateColumns: `repeat(${displayTypes.length}, minmax(0, 1fr))` }}>

                    {displayTypes.map((typeItem) => {
                        const entry = getItemByTypeItem(typeItem);

                        if (!entry) {
                            return (
                                <div key={typeItem.fieldName} className='rounded-lg border p-12 flex items-center justify-between gap-12'>
                                    <div className='text-xs font-semibold uppercase tracking-wide opacity-60'>{typeItem.name || typeItem.fieldName}</div>
                                    <div className='text-xs opacity-40'>No file</div>
                                </div>
                            );
                        }

                        return (
                            <a
                                key={typeItem.fieldName}
                                href={getUrl(entry.type)}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='rounded-lg border p-12 flex flex-col gap-8 text-current no-underline transition-colors hover:bg-gray-50 hover:border-gray-400'
                            >
                                <div className='flex items-center gap-8 min-w-0'>
                                    <div className='text-xs font-semibold uppercase tracking-wide opacity-60'>{typeItem.name || typeItem.fieldName}</div>
                                    <div className='text-xs truncate text-gray-800'>{entry.filename}</div>
                                </div>

                                <div className='flex items-center gap-4 text-xs font-normal whitespace-nowrap'>
                                    <FaFileDownload className='text-xs opacity-20' />
                                    <span className='opacity-50 font-medium'>Download</span>
                                </div>
                            </a>
                        );
                    })}

                </div>

            </div>

            <div className='flex items-center p-8 border-l'>
                <IconButton aria-label='Delete version' color='inherit' onClick={handleDelete} disabled={isDeleting}>
                    <FaTrash className='text-sm opacity-80' />
                </IconButton>
            </div>

        </div>
    );
}

export default EntriesListComponent;
